<script setup lang="ts">
import { Head, usePage } from '@inertiajs/vue3'
import SecretForm from '@/Components/Secret/SecretForm.vue'
import SecretFormHome from '@/Components/Secret/SecretFormHome.vue'
import Slider from '@/Components/Slider.vue'
import TextSlide from '@/Components/TextSlide.vue'
import Badges from '@/Components/Badges.vue'
import FeatureGrid from '@/Components/FeatureGrid.vue'
import PricingTable from '@/Components/PricingTable.vue'
import Faq from '@/Components/Faq.vue'
import { computed, onMounted, ref } from 'vue'
import KeyvisualImage from '$img/Keyvisual-Start.webp'

defineProps<{
    canLogin?: boolean
    canRegister?: boolean
    laravelVersion: string
    phpVersion: string
}>()

const howToSteps: { title: string; description: string }[] = [
    {
        title: 'Daten eingeben',
        description: 'Nach dem Login geben Sie das zu teilende Passwort bzw. das gesamte zu verschlüsselnde Geheimnis in das Textfeld.',
    },
    {
        title: 'Schutz wählen',
        description:
            'Sie wählen aus, ob der Empfänger das Passwort speichern darf oder nicht und wie lange er Zugriff auf das Passwort haben soll. Nach Ablauf dieser Zeit wird es automatisch vollständig gelöscht.',
    },
    {
        title: 'Optionaler Passwortschutz',
        description:
            'Wenn Sie zusätzliche Sicherheit wünschen, können Sie ein Passwort hinterlegen, dass der Empfänger eingeben muss, um das verschlüsselte Passwort anzuzeigen.',
    },
    {
        title: 'Link generieren und abschicken',
        description:
            'Jetzt kopieren Sie den generierten Link und fügen ihn in Ihre Nachricht an den Empfänger ein (z. B. in eine E-Mail). Über diesen Link kann der Empfänger das Passwort abrufen und erhält Informationen, was er mit dieser Information tun darf und was nicht (z. B. speichern).',
    },
    {
        title: 'Passwort sicher geteilt',
        description: 'Fertig. Das Geheimnis wird später zum eingestellten Zeitpunkt automatisch vollständig gelöscht.',
    },
]

const brands: { src: string; alt: string; width: number }[] = [
    {
        src: '/img/brands/Bytehaus.webp',
        alt: 'Bytehaus',
        width: 187,
    },
    {
        src: '/img/brands/DevLabor.webp',
        alt: 'DevLabor',
        width: 219,
    },
    {
        src: '/img/brands/WP-Profi.webp',
        alt: 'WP-Profi',
        width: 111,
    },
]

const container = ref(null)
const user = computed(() => usePage().props.auth.user)

const mounted = ref(false)
onMounted(() => {
    mounted.value = true
})
</script>

<template>
    <Head title="Dateien & Passwörter Ende-zu-Ende verschlüsselt teilen" />

    <div
        id="Ausprobieren"
        ref="container"
        class="teaser mx-auto"
    >
        <h1 class="text-center text-5xl font-bold sm:text-6xl md:text-7xl lg:text-8xl">
            <TextSlide
                class="mb-2 inline-flex"
                :words="['Passwörter', 'Zugangsdaten', 'Geheimnisse', 'Notizen', 'Keys']"
            />
            <br class="md:hidden" />
            <span class="text-primary-500 sm:py-0">
                einfach<br class="hidden md:block" />
                und sicher
            </span>
            <br class="hidden xs:block" />teilen
        </h1>
        <p
            v-if="!user"
            class="mx-auto mt-9 max-w-3xl text-center text-lg md:text-xl"
        >
            Wer Zugangsdaten teilt, will sie sicher versenden, ohne sie ungeschützt in Mails oder Chatverläufen zu hinterlassen.
            Sicher-Teilen.de ermöglicht <span class="font-bold">verschlüsselten Versand</span> mit
            <span class="font-bold">voller Kontrolle</span> und
            <span class="font-bold">einfacher Handhabung für Sender und Empfänger.</span>
        </p>
        <div
            v-if="mounted"
            class="test relative mt-4 pt-12"
        >
            <SecretForm v-if="user" />
            <SecretFormHome v-else />
        </div>
        <div v-else>
            <SecretForm v-if="user" />
            <SecretFormHome v-else />
        </div>
        <div class="mt-8">
            <Badges />
        </div>
    </div>
    <template v-if="!user || !user.is_subscribed">
        <div
            id="Sicherheitsvorteile"
            class="mx-auto max-w-[1216px] pt-36"
        >
            <div class="block space-x-24 lg:flex">
                <div class="basis-1/2">
                    <span class="uppercase text-primary-500">Sicherheitsvorteile</span>
                    <h2 class="mt-2 text-4xl font-bold text-gray-900">
                        Zugangsdaten endlich<br />
                        einfach und sicher teilen
                    </h2>
                    <p class="font-lg my-8">
                        Praxisorientierte Sicherheit heißt nicht, dass es nicht noch sicherer gehen würde. Sicher-Teilen.de will eine
                        Möglichkeit bieten, mit der Zugangsdaten einfach und sicher geteilt werden können. In diesem Rahmen und mit diesem
                        Ziel tun wir alles, um Ihnen ein sicheres Teilen zu ermöglichen – von Praktikern für Praktiker.
                    </p>
                    <a
                        v-if="user"
                        href="#Preise"
                        class="text-primary-500 underline decoration-transparent outline-none transition-all hover:decoration-current focus:decoration-current"
                    >
                        Jetzt auf PRO upgraden
                        <i class="fa-light fa-angle-right"></i>
                    </a>
                    <a
                        v-else
                        href="#Ausprobieren"
                        class="text-primary-500 underline decoration-transparent outline-none transition-all hover:decoration-current focus:decoration-current"
                    >
                        Jetzt KOSTENLOS ausprobieren
                        <i class="fa-light fa-angle-right"></i>
                    </a>
                </div>
                <div
                    v-if="mounted"
                    class="basis-1/2"
                >
                    <img
                        class="hidden sm:block"
                        :src="KeyvisualImage"
                        alt=""
                    />
                </div>
            </div>
        </div>
        <div class="mx-auto mt-20 max-w-[1216px] sm:mt-28">
            <FeatureGrid :show-badges="false" />
        </div>
        <div
            v-if="mounted"
            class="mt-8 flex w-full sm:hidden"
        >
            <img
                class="w-full"
                :src="KeyvisualImage"
                alt=""
            />
        </div>
        <div
            id="SoEinfachFunktionierts"
            class="mt-28 bg-gray-900 py-28 sm:mt-32 sm:py-32"
        >
            <div class="mx-auto max-w-[1216px] text-white">
                <div class="block space-x-0 lg:flex lg:space-x-24">
                    <div class="mb-14 basis-1/2 lg:mb-0">
                        <span class="uppercase text-primary-500">In 5 Schritten</span>
                        <h2 class="mt-2 text-4xl font-bold">So einfach funktioniert's</h2>
                        <p class="font-lg my-8">
                            Mit Sicher teilen übermitteln Sie Passwörter oder vertrauliche Informationen ganz einfach und maximal geschützt.
                            Unser Tool führt Sie Schritt für Schritt durch den Prozess: Vom Eingeben der Daten über die Auswahl der
                            gewünschten Schutzmechanismen bis hin zum Versand eines verschlüsselten Links. So stellen Sie sicher, dass Ihre
                            sensiblen Informationen nur da ankommen, wo sie hingehören – und automatisch wieder verschwinden, wenn sie nicht
                            mehr benötigt werden.
                        </p>
                        <a
                            v-if="user"
                            href="#Preise"
                            class="text-primary-500 underline decoration-transparent outline-none transition-all hover:decoration-current focus:decoration-current"
                        >
                            Jetzt auf PRO upgraden
                            <i class="fa-light fa-angle-right"></i>
                        </a>
                        <a
                            v-else
                            href="#Ausprobieren"
                            class="text-primary-500 underline decoration-transparent outline-none transition-all hover:decoration-current focus:decoration-current"
                        >
                            Jetzt KOSTENLOS ausprobieren
                            <i class="fa-light fa-angle-right"></i>
                        </a>
                    </div>
                    <div class="basis-1/2 space-y-10">
                        <div
                            v-for="(step, index) in howToSteps"
                            :key="index"
                            class="flex space-x-4"
                        >
                            <div class="h-max w-max rounded-[50%] bg-primary-500 px-6 py-4 text-center text-xl font-bold text-white">
                                {{ index + 1 }}
                            </div>
                            <div class="mt-4">
                                <span class="text-xl font-bold">{{ step.title }}</span>
                                <p class="mt-2">
                                    {{ step.description }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div
            id="Preise"
            class="py-28 sm:py-32"
        >
            <PricingTable />
        </div>
        <div class="bg-gray-100 py-16 text-center sm:py-16">
            <p class="pb-16 text-lg font-light uppercase text-gray-900">Diese Unternehmen vertrauen auf Sicher-Teilen.de:</p>
            <Slider>
                <template
                    v-for="(brand, index) in brands"
                    :key="index"
                >
                    <img
                        :width="brand.width"
                        height="40"
                        class="mx-12 h-[40px]"
                        :src="brand.src"
                        :alt="brand.alt"
                    />
                </template>
            </Slider>
        </div>
    </template>
    <div
        id="FAQ"
        class="pt-28 sm:pt-32"
    >
        <Faq />
    </div>
</template>
<style scoped>
.test:before {
    content: '';
    background-image: url('$img/Keyvisual-Start.webp');
    background-repeat: no-repeat;
    background-position: center;
    transform: var(--transform);
    transition: '.3s ease-out all';
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;
    z-index: -1;
}
</style>
